import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCompanyInfo } from "store/companySlice";
import { clearCurrentBoard } from "store/currentBoardSlice";

import Api from "api/Api";
import ContentBox from "layout/atoms/ContentBox";
import MainContainer from "layout/atoms/MainContainer";

import GridBoard from "layout/atoms/GridBoard";
import Label from "layout/atoms/Label";
import Loader from "layout/atoms/Loader";
import EmptyState from "layout/molecules/EmptyState";
import PersonList from "layout/molecules/PersonList";

import {
  StatCard,
  StatsGrid,
  UserSummary,
  UserTableHeader,
} from "./UsersListStyles";

function UsersList() {
  const dispatch = useDispatch();
  let api = new Api();
  const companyInfo = useSelector(selectCompanyInfo);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const fetchUsers = async () => {
    setIsLoading(true);
    const companyId = companyInfo.id;

    const fetchedUsers = await api.getUsers(companyId).then((newUsers) => {
      setIsLoading(false);
      setUsers(newUsers);
    });
  };

  useEffect(() => {
    dispatch(clearCurrentBoard());
    fetchUsers();
  }, [dispatch]);

  const getUserStats = () => {
    const totalUsers = users.length;
    const ssoUsers = users.filter((user) => user.kind === "SSO").length;
    const regularUsers = users.filter((user) => user.kind !== "SSO").length;

    return { totalUsers, ssoUsers, regularUsers };
  };

  const { totalUsers, ssoUsers, regularUsers } = getUserStats();

  return (
    <MainContainer
      topbar={
        <div>
          <h2>{t("usersList.title")}</h2>
        </div>
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <GridBoard>
          {users.length !== 0 ? (
            <>
              <ContentBox>
                <UserSummary>
                  <h3>{t("usersList.summary.title")}</h3>
                  <StatsGrid>
                    <StatCard>
                      <div className="number">{totalUsers}</div>
                      <div className="label">
                        {t("usersList.summary.total")}
                      </div>
                    </StatCard>
                    <StatCard>
                      <div className="number">{ssoUsers}</div>
                      <div className="label">{t("usersList.summary.sso")}</div>
                    </StatCard>
                    <StatCard>
                      <div className="number">{regularUsers}</div>
                      <div className="label">
                        {t("usersList.summary.regular")}
                      </div>
                    </StatCard>
                  </StatsGrid>
                </UserSummary>
              </ContentBox>

              <ContentBox>
                <UserTableHeader>
                  <div className="avatar-col"></div>
                  <div className="name-col">{t("usersList.table.name")}</div>
                  <div className="type-col">{t("usersList.table.type")}</div>
                </UserTableHeader>
                <PersonList
                  items={[...users]
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((user) => ({
                      id: user.id,
                      name: user.displayName || "John Doe",
                      subtitle: user.email,
                      avatarUrl:
                        user.photoUrl || "/assets/illustrations/avatar.png",
                      badge: user.kind && (
                        <Label
                          status={user.kind === "SSO" ? "OPEN" : "COMPLETE"}
                        >
                          {user.kind}
                        </Label>
                      ),
                    }))}
                />
              </ContentBox>
            </>
          ) : (
            <EmptyState
              type="noUsers"
              header={t("emptyStates.users.header")}
              subheader={t("emptyStates.users.subheader")}
            />
          )}
        </GridBoard>
      )}
    </MainContainer>
  );
}

export default UsersList;
